import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';
import { connect } from 'react-redux';
import { graphql } from '@apollo/react-hoc';
import { compose } from 'react-recompose';

import { startCase } from 'lodash/string';
import Form from '../../../../components/Form';
import Button from '../../../../components/Button';
import Modal from '../../../../components/Modal';
import ImageDropZoneWithCrop from '../../ImageDropZoneWithCrop';
import Icon from '../../../../components/Icon';
import Loader from '../../../../components/Loader';
import uploadPhotoMutation from '../../mutations/uploadPremiumHEMProfileImageMutation';
import { SimpleLoadingComponent } from '../../../../utils/apollo';
import {
  addProfilePhotoFile,
  addProfilePhotoCroppedImageBlob,
  addProfilePhotoS3ScannedURI,
} from '../../actions';
import messages from './messages';
import { COLLEGE_PROFILE_TYPE_NAME_ALUMNI,
  COLLEGE_PROFILE_TYPE_NAME_FACULTY,
  COLLEGE_PROFILE_TYPE_NAME_STUDENT,
  COLLEGE_PROFILE_TYPE_NAME_PROGRAM } from '../../constants';
import ProfileStudentForm from './forms/profileStudentForm';
import ProfileAlumniForm from './forms/profileAlumniForm';
import ProfileFacultyForm from './forms/profileFacultyForm';
import ProfileProgramForm from './forms/profileProgramForm';
import elementIds from './elementIds';
import './styles.css';

export const modalSteps = {
  EDIT_PROFILE: 1,
  EDIT_PROFILE_PHOTO: 2,
};

function validateFields(formData, type) {
  // WYSIWYG when erasing text has this string
  // https://github.com/quilljs/quill/issues/1328
  const body = formData.body.replace('<p><br /></p>', '');
  const { personsName, _class, quote, hometown, major, programName } = formData;

  switch (type) {
    case COLLEGE_PROFILE_TYPE_NAME_FACULTY:
      return {
        personsName: !personsName.trim(),
        quote: !quote.trim(),
        body: !body.trim(),
      };
    case COLLEGE_PROFILE_TYPE_NAME_ALUMNI:
      return {
        personsName: !personsName.trim(),
        graduation_year: !_class.trim(),
        major: !major.trim(),
        quote: !quote.trim(),
        body: !body.trim(),
      };
    case COLLEGE_PROFILE_TYPE_NAME_PROGRAM:
      return {
        programName: !programName.trim(),
        quote: !quote.trim(),
        body: !body.trim(),
      };
    default:
      return {
        personsName: !personsName.trim(),
        hometown: !hometown.trim(),
        major: !major.trim(),
        quote: !quote.trim(),
        body: !body.trim(),
      };
  }
}

/**
 * This component is used to create the form for EDITING an existint Profile AND ADDING a new Profile.
 */
export class AddProfileFormComponent extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
    profile: PropTypes.object,
    profileIndex: PropTypes.number,
    updateHubsCustomProfiles: PropTypes.func.isRequired,
    removeCustomProfile: PropTypes.func,
    open: PropTypes.bool,
    photoUpload: PropTypes.func,
    addProfilePhotoFile: PropTypes.func,
    addProfilePhotoCroppedImageBlob: PropTypes.func,
    addProfilePhotoS3ScannedURI: PropTypes.func,
    customProfilesCount: PropTypes.number,
    scannedPhotos: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  getInitialState = () => {
    const { profile } = this.props;
    return {
      personsName: get(profile, 'persons_name', ''),
      hometown: get(profile, 'hometown', ''),
      major: get(profile, 'major', ''),
      quote: get(profile, 'quote', ''),
      body: get(profile, 'body', ''),
      favActivities: (get(profile, 'fav_activities', ['']) || /* istanbul ignore next */ [''])[0],
      favHangout: get(profile, 'fav_hangout', ''),
      bestPlaceToEat: get(profile, 'best_place_to_eat', ''),
      favEvent: get(profile, 'fav_event', ''),
      favTradition: get(profile, 'fav_tradition', ''),
      travel: get(profile, 'travel', ''),
      imgDisplayUrl: get(profile, 'imgDisplayUrl', ''),
      img: get(profile, 'img', ''),
      imgThumbLarge: get(profile, 'img_thumb_large', ''),
      logo: get(profile, 'img_thumb_large', ''),
      headline: get(profile, 'headline', ''),
      _class: get(profile, 'class', ''),
      department: get(profile, 'department', ''),
      desc: get(profile, 'desc', ''),
      programName: get(profile, 'program_name', ''),
      addFacts: get(profile, 'add_facts', ''),
      logoDisplayUrl: get(profile, 'logoDisplayUrl', ''),
      productId: get(profile, 'product_id', null),
      isEditMode: !!profile,
      errors: {},
      success: false,
      currentStep: modalSteps.EDIT_PROFILE,
      uploadingPhoto: false,
      draftPhotoUrl: '',
    };
  };

  onChange = (e) => {
    const value = e.target ? e.target.value : e;
    const name = e.target ? e.target.name : 'body'; // the case for WYSIWYG

    this.setState({ [name]: value });
  };

  _validateForm = (event) => {
    // eslint-disable-next-line react/no-access-state-in-setstate
    const { type, updateHubsCustomProfiles, profileIndex, customProfilesCount } = this.props;
    const errors = validateFields(this.state, type);
    const { personsName, hometown, major, quote, body, favActivities, favHangout,
      bestPlaceToEat, favEvent, favTradition, travel, imgThumbLarge, img, logo,
      headline, _class, department, desc, programName, addFacts, draftPhotoUrl, productId,
    } = this.state;

    const errorKeys = Object.keys(errors);
    /* istanbul ignore else */
    if (errorKeys.some((key) => errors[key])) {
      this.setState({ errors }, () => {
        const id = errorKeys.find((key) => errors[key]);
        const input = document.getElementById(id);
        if (input) {
          input.focus();
        }
      });
      event.preventDefault();
    } else {
      updateHubsCustomProfiles({
        persons_name: personsName,
        hometown,
        major,
        quote,
        body,
        fav_activities: [favActivities],
        fav_hangout: favHangout,
        best_place_to_eat: bestPlaceToEat,
        fav_event: favEvent,
        fav_tradition: favTradition,
        travel,
        img,
        img_thumb_large: imgThumbLarge,
        type,
        logo,
        headline,
        class: _class,
        department,
        desc,
        program_name: programName,
        add_facts: addFacts,
        product_id: productId,
      });
      const selectedIndex = (!isNil(profileIndex)) ? profileIndex : customProfilesCount;

      if (!isEmpty(draftPhotoUrl)) {
        this.props.addProfilePhotoS3ScannedURI(selectedIndex, draftPhotoUrl);
      }
      this.props.onClose();
    }
  };

  _close = () => {
    this.props.onClose();
  };

  showImageDropZone = () => {
    this.setState({ currentStep: modalSteps.EDIT_PROFILE_PHOTO });
  }

  goBackFromImageDropZone = () => {
    this.setState({ currentStep: modalSteps.EDIT_PROFILE });
  }

  previewPhotoAndGoBack = async (ImageState) => {
    const { croppedImageBlobDataStr, croppedImageBlob } = ImageState;
    this.setState({ uploadingPhoto: true });
    try {
      const uploadResult = await this.props.photoUpload({
        variables: {
          input: {
            imgDataStr: croppedImageBlobDataStr,
          },
        },
      });
      this.props.addProfilePhotoFile(croppedImageBlobDataStr);
      this.props.addProfilePhotoCroppedImageBlob(croppedImageBlob);
      this.setState({ uploadingPhoto: false });
      if (!get(uploadResult, 'data.uploadPremiumHEMProfileImage.scanAndUploadSuccess')) {
        return;
      } else {
        this.setState({ draftPhotoUrl: get(uploadResult, 'data.uploadPremiumHEMProfileImage.url') });
      }
    } catch (err) {
      this.setState({ uploadingPhoto: false });
      return;
    }

    this.goBackFromImageDropZone();
  }

  render() {
    const { intl, type, profile, removeCustomProfile, scannedPhotos, profileIndex } = this.props;
    const { formatMessage } = intl;
    const { personsName, programName, _class, hometown, major, quote, body, favActivities, favHangout,
      bestPlaceToEat, favEvent, favTradition, travel, isEditMode, errors, currentStep, uploadingPhoto, draftPhotoUrl } = this.state;
    const displayUrl =
      (draftPhotoUrl || scannedPhotos[profileIndex]) ||
      get(profile, 'img');

    const hasChanges = !isEmpty(profile &&
      (draftPhotoUrl ||
      Object.keys(this.state).filter((a) =>
        snakeCase(a) in profile &&
          (a === 'favActivities' ? (profile[snakeCase(a)][0] !== this.state[a]) : (profile[snakeCase(a)] !== this.state[a])))));

    switch (currentStep) {
      case modalSteps.EDIT_PROFILE:
        return (
          <React.Fragment>
            <Form
              onSubmit={this._validateForm}
              method="post"
              target="_self"
              autoComplete="off"
              styleName="no-margin"
              stackable="true"
              id={elementIds.updateProfileForm}
            >
              <Modal.Header id={elementIds.header} styleName="add-profile-modal-header">
                { isEditMode ?
                  <span>Edit { startCase(type) } Spotlight</span> :
                  <span>Create {type === COLLEGE_PROFILE_TYPE_NAME_ALUMNI ? 'an' : 'a'} { startCase(type) } Spotlight</span>}
                <div>
                  <Button
                    type="button"
                    color="grey"
                    styleName="profile-photo-oval"
                    style={{ backgroundImage: `url(${displayUrl})` }}
                    onClick={this.showImageDropZone}
                  >
                    {!displayUrl && (
                      <p styleName="profile-photo-upload-text">
                        <FormattedMessage {...messages.upload_photo} />
                      </p>
                    )}
                  </Button>
                  {displayUrl && (
                  <div styleName="edit-profile-photo-button">
                    <Button
                      type="button"
                      basic
                      onClick={this.showImageDropZone}
                    >
                      <FormattedMessage {...messages.edit_photo} />
                    </Button>
                  </div>
                  )}
                </div>
              </Modal.Header>
              <Modal.Content styleName="add-profile-modal-content">
                { type === COLLEGE_PROFILE_TYPE_NAME_STUDENT && (
                  <ProfileStudentForm
                    personsName={personsName}
                    hometown={hometown}
                    major={major}
                    quote={quote}
                    body={body}
                    favActivities={favActivities}
                    favHangout={favHangout}
                    bestPlaceToEat={bestPlaceToEat}
                    favEvent={favEvent}
                    favTradition={favTradition}
                    travel={travel}
                    errors={errors}
                    onChange={this.onChange}
                    formatMessage={formatMessage}
                    type={type}
                  />
                )}
                { type === COLLEGE_PROFILE_TYPE_NAME_ALUMNI && (
                  <ProfileAlumniForm
                    personsName={personsName}
                    hometown={hometown}
                    _class={_class}
                    major={major}
                    quote={quote}
                    body={body}
                    errors={errors}
                    onChange={this.onChange}
                    formatMessage={formatMessage}
                    type={type}
                  />
                )}
                { type === COLLEGE_PROFILE_TYPE_NAME_FACULTY && (
                  <ProfileFacultyForm
                    personsName={personsName}
                    quote={quote}
                    body={body}
                    errors={errors}
                    onChange={this.onChange}
                    formatMessage={formatMessage}
                    type={type}
                  />
                )}
                { type === COLLEGE_PROFILE_TYPE_NAME_PROGRAM && (
                  <ProfileProgramForm
                    programName={programName}
                    quote={quote}
                    body={body}
                    errors={errors}
                    onChange={this.onChange}
                    formatMessage={formatMessage}
                    type={type}
                  />
                )}
              </Modal.Content>
              <Modal.Actions styleName="actions-modal">
                {!isEmpty(this.props.profile) && (
                  <div styleName="profile-left-button">
                    <Button id={elementIds.deleteBtn} negative content="Delete Spotlight" type="button" onClick={removeCustomProfile} />
                  </div>
                )}
                <div styleName="profile-right-button">
                  <Button id={elementIds.cancelBtn} type="button" content="Cancel" onClick={this._close} />
                  <Button
                    id={elementIds.submitBtn}
                    disabled={isEditMode && !hasChanges}
                    type="submit"
                    primary
                    content="Preview and continue editing"
                  />
                </div>
              </Modal.Actions>
            </Form>
          </React.Fragment>
        );
      case modalSteps.EDIT_PROFILE_PHOTO:
        return (
          <React.Fragment>
            { uploadingPhoto ? (
              <div styleName="uploading-image-spinner">
                <Icon name="image outline" color="teal" size="massive" />
                <Loader active inline />
                <FormattedMessage {...messages.imageUploading} />
              </div>
            ) : (
              <ImageDropZoneWithCrop
                crop={{
                  aspect: 1,
                  width: 50,
                  height: 50,
                  x: 0,
                  y: 0,
                }}
                headerText={messages.cropPhotoInstructionsCircle}
                nevermindAction={this.goBackFromImageDropZone}
                nextButtonText={messages.continueEditingProfile}
                nextButtonAction={this.previewPhotoAndGoBack}
                uploaderInstructions1={messages.editProfilePictureInstructions1}
                uploaderInstructions2={messages.editProfilePictureInstructions2}
                roundCropper
                allowedTypes={['image/png', 'image/jpeg', 'image/jpg']}
              />
            )}
          </React.Fragment>
        );
      default:
        return (<Modal.Content><div id="profile-invalid-step"><h3>Something went wrong...</h3></div></Modal.Content>);
    }
  }
}

const mapStateToProps = (state) => ({
  customProfilesCount: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.customProfiles.length', 0),
  scannedPhotos: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.draftImagesData.scannedPhotoUrls', {}),
});

const uploadPhoto = graphql(uploadPhotoMutation, {
  name: 'photoUpload',
});

export default compose(
  connect(mapStateToProps, {
    addProfilePhotoFile,
    addProfilePhotoCroppedImageBlob,
    addProfilePhotoS3ScannedURI,
  }),
  SimpleLoadingComponent(uploadPhoto),
)(injectIntl(AddProfileFormComponent));
